/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import PopupDialog from "../PopupDialog";
import "./AppDependency.css";

const ANDROID_PACKAGE_REGEX = /^[a-zA-Z]\w*(\.[a-zA-Z]\w*)+$/;
const isValidPackageId = (id) => {
  const t = id.trim();
  return t.length > 0 && t.length <= 200 && ANDROID_PACKAGE_REGEX.test(t);
};

function AppDependency({ postCallTheNewSubmission }) {
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails,
  );

  const isDraft = submissionData?.status === "DRAFT";

  const [dependencies, setDependencies] = useState(
    submissionData?.appDependencyInfo || [],
  );
  const [packageId, setPackageId] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editPackageId, setEditPackageId] = useState("");
  const [editVersionCode, setEditVersionCode] = useState("");
  const [showPackageFormatInfo, setShowPackageFormatInfo] = useState(false);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (submissionData?.appDependencyInfo) {
      setDependencies(submissionData.appDependencyInfo);
    }
  }, [submissionData?.appDependencyInfo]);

  const isAddEnabled = isValidPackageId(packageId);
  const isPackageInvalid =
    packageId.trim() !== "" && !isValidPackageId(packageId);

  const handleAdd = async () => {
    if (!isAddEnabled) return;

    const newDependency = {
      packageId: packageId.trim(),
      versionCode: Number(versionCode.trim()),
    };

    const updatedDependencies = [...dependencies, newDependency];

    const newSubmissionData = { ...submissionData };
    newSubmissionData.appDependencyInfo = updatedDependencies;
    delete newSubmissionData["legalDocuments"];
    try {
      await postCallTheNewSubmission(newSubmissionData);
      setPackageId("");
      setVersionCode("");
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to save app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    }
  };

  const handleEdit = (index) => {
    const dep = dependencies[index];
    setEditIndex(index);
    setEditPackageId(dep.packageId);
    setEditVersionCode(String(dep.versionCode));
  };

  // SDD111MARKETPLACE1389R121:
  // CODE111_MARKETPLACE_USSTORY_1389_R1-21: Handle the save action for editing an app dependency, updating the submission data and managing errors.

  const handleEditSave = async () => {
    if (editIndex === null) return;
    const code = Number(editVersionCode.trim());
    if (!isValidPackageId(editPackageId)) return;

    const updatedDependencies = dependencies.map((dep, i) =>
      i === editIndex
        ? { packageId: editPackageId.trim(), versionCode: code }
        : dep,
    );

    const newSubmissionData = { ...submissionData };
    newSubmissionData.appDependencyInfo = updatedDependencies;
    delete newSubmissionData["legalDocuments"];
    try {
      await postCallTheNewSubmission(newSubmissionData);
      setEditIndex(null);
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to update app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    }
  };

  const handleEditCancel = () => {
    setEditIndex(null);
  };

  const handleDelete = async (index) => {
    const updatedDependencies = dependencies.filter((_, i) => i !== index);

    const newSubmissionData = { ...submissionData };
    newSubmissionData.appDependencyInfo = updatedDependencies;
    delete newSubmissionData["legalDocuments"];
    try {
      await postCallTheNewSubmission(newSubmissionData);
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to delete app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    }
  };

  return (
    <div className="app-dependency">
      <div className="app-dependency-title">App Dependency</div>
      <div className="app-dependency-subtitle">
        Specify any external or companion applications and the respective
        versions required for this app to function properly.
      </div>

      <div className="app-dependency-form">
        <div className="app-dependency-field">
          <label htmlFor="app-dep-package-id">Select Package</label>
          <input
            id="app-dep-package-id"
            type="text"
            placeholder="App Name/Title"
            value={packageId}
            onChange={(e) => setPackageId(e.target.value)}
            disabled={!isDraft}
            maxLength={200}
          />
          {isPackageInvalid && (
            <div className="app-dependency-helper-text">
              Please follow the valid format,{" "}
              <button
                type="button"
                className="app-dependency-helper-link"
                onClick={() => setShowPackageFormatInfo((prev) => !prev)}
              >
                click here
              </button>{" "}
              to see.
            </div>
          )}
          {isPackageInvalid && showPackageFormatInfo && (
            <div className="app-dependency-format-popover">
              <div className="app-dependency-format-title">Valid format</div>
              <ul>
                <li>At least two segments separated by dots (.)</li>
                <li>Each segment starts with a letter</li>
                <li>Only letters, numbers, and underscores</li>
                <li>No spaces or special characters</li>
              </ul>
            </div>
          )}
        </div>

        <div className="app-dependency-field">
          <label htmlFor="app-dep-version-code">App Version</label>
          <input
            id="app-dep-version-code"
            type="text"
            placeholder="Version Code"
            value={versionCode}
            onChange={(e) => setVersionCode(e.target.value)}
            disabled={!isDraft || packageId.trim() === ""}
          />
        </div>

        <button
          className="app-dependency-add-btn"
          onClick={handleAdd}
          disabled={!isDraft || !isAddEnabled}
        >
          Add
        </button>
      </div>

      {dependencies.length > 0 && (
        <div className="app-dependency-table-container">
          <table className="app-dependency-table">
            <thead>
              <tr>
                <th>Package</th>
                <th>App Version</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dependencies.map((dep, index) => {
                const isEditing = editIndex === index;

                if (isEditing) {
                  return (
                    <tr
                      key={`edit-${dep.packageId}-${dep.versionCode}`}
                      className="app-dependency-row-editing"
                    >
                      <td>
                        <div className="app-dependency-inline-field">
                          <div className="app-dependency-inline-select-wrapper">
                            <input
                              type="text"
                              value={editPackageId}
                              onChange={(e) => setEditPackageId(e.target.value)}
                              placeholder="Package"
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="app-dependency-inline-field">
                          <div className="app-dependency-inline-select-wrapper">
                            <input
                              type="text"
                              value={editVersionCode}
                              onChange={(e) =>
                                setEditVersionCode(e.target.value)
                              }
                              placeholder="Version"
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="app-dependency-actions">
                          <button
                            onClick={handleEditSave}
                            aria-label="Save"
                            className="app-dependency-action-save"
                            disabled={!isValidPackageId(editPackageId)}
                          >
                            <svg
                              width="16"
                              height="12"
                              viewBox="0 0 16 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5.59509 11.8037L0 6.20859L1.39877 4.80982L5.59509 9.00613L14.6012 0L16 1.39877L5.59509 11.8037Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={handleEditCancel}
                            aria-label="Cancel"
                            className="app-dependency-action-cancel"
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.40351 10L0 8.59649L3.60902 4.98747L0 1.40351L1.40351 0L5.01253 3.60902L8.59649 0L10 1.40351L6.39098 4.98747L10 8.59649L8.59649 10L5.01253 6.39098L1.40351 10Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={`${dep.packageId}-${dep.versionCode}`}>
                    <td className="ad-text-cell ad-package-cell">
                      <span className="ad-cell-text" title={dep.packageId}>
                        {dep.packageId}
                      </span>
                    </td>

                    <td className="ad-text-cell ad-version-cell">
                      <span
                        className="ad-cell-text"
                        title={String(dep.versionCode)}
                      >
                        {dep.versionCode}
                      </span>
                    </td>

                    <td>
                      <div className="app-dependency-actions">
                        <button
                          onClick={() => handleEdit(index)}
                          disabled={!isDraft}
                          aria-label="Edit"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.4863 4.73699L9.23066 1.51507L10.3031 0.441096C10.5968 0.147032 10.9574 0 11.3851 0C11.8128 0 12.1735 0.147032 12.4671 0.441096L13.5396 1.51507C13.8332 1.80913 13.9864 2.16393 13.9992 2.57945C14.012 2.99498 13.8715 3.34977 13.5779 3.64384L12.4863 4.73699ZM11.3755 5.86849L3.25563 14H0V10.7397L8.11992 2.60822L11.3755 5.86849Z"
                              fill="#121415"
                              fillOpacity="0.6"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          disabled={!isDraft}
                          aria-label="Delete"
                        >
                          <svg
                            width="15"
                            height="16"
                            viewBox="0 0 15 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2.66667 16C2.17778 16 1.75926 15.8259 1.41111 15.4778C1.06296 15.1296 0.888889 14.7111 0.888889 14.2222V2.66667H0V0.888889H4.44444V0H9.77778V0.888889H14.2222V2.66667H13.3333V14.2222C13.3333 14.7111 13.1593 15.1296 12.8111 15.4778C12.463 15.8259 12.0444 16 11.5556 16H2.66667ZM11.5556 2.66667H2.66667V14.2222H11.5556V2.66667ZM4.44444 12.4444H6.22222V4.44444H4.44444V12.4444ZM8 12.4444H9.77778V4.44444H8V12.4444Z"
                              fill="#121415"
                              fillOpacity="0.6"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage="Error"
        componentName="appError"
        onClose={() => setOpenErrorPopup(false)}
      />
    </div>
  );
}

AppDependency.propTypes = {
  postCallTheNewSubmission: PropTypes.func.isRequired,
};

export default AppDependency;
